/**
 * Generated by orval v8.11.0 🍺
 * Do not edit manually.
 * Core API
 * Core API Documentation
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  GetIncidentForPrintQueryControllerGetParams,
  GetIncidentPdfResponseDto,
  IncidentDetailResponseDto,
  ListIncidentsQueryControllerListParams,
  ListIncidentsResponseDto,
  SignOffIncidentDto,
  SignOffIncidentResponseDto,
} from "../model";

import { axiosClient } from "../../lib/api";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Closes the incident, records resolvedBy/resolvedAt, and stores optional free-text remedial-action notes. Idempotency: signing off an already-resolved incident returns 409. Sign-off is locked (409) until the latest reading in the incident window is back in range.
 * @summary Manager signs off an open out-of-range incident
 */
export const signOffIncidentCommandControllerSignOff = (
  incidentId: string,
  signOffIncidentDto: SignOffIncidentDto,
  signal?: AbortSignal,
) => {
  return axiosClient<SignOffIncidentResponseDto>({
    url: `/v1/out-of-range-incidents/${incidentId}/sign-off`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: signOffIncidentDto,
    signal,
  });
};

export const getSignOffIncidentCommandControllerSignOffMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>,
    TError,
    { incidentId: string; data: SignOffIncidentDto },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>,
  TError,
  { incidentId: string; data: SignOffIncidentDto },
  TContext
> => {
  const mutationKey = ["signOffIncidentCommandControllerSignOff"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>,
    { incidentId: string; data: SignOffIncidentDto }
  > = (props) => {
    const { incidentId, data } = props ?? {};

    return signOffIncidentCommandControllerSignOff(incidentId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SignOffIncidentCommandControllerSignOffMutationResult = NonNullable<
  Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>
>;
export type SignOffIncidentCommandControllerSignOffMutationBody =
  SignOffIncidentDto;
export type SignOffIncidentCommandControllerSignOffMutationError = void;

/**
 * @summary Manager signs off an open out-of-range incident
 */
export const useSignOffIncidentCommandControllerSignOff = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>,
      TError,
      { incidentId: string; data: SignOffIncidentDto },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof signOffIncidentCommandControllerSignOff>>,
  TError,
  { incidentId: string; data: SignOffIncidentDto },
  TContext
> => {
  return useMutation(
    getSignOffIncidentCommandControllerSignOffMutationOptions(options),
    queryClient,
  );
};
/**
 * Returns the incident metadata, the triggering first reading, and all readings on the fridge within the incident window (chronological). For closed incidents the window ends at resolvedAt; for open incidents at now.
 * @summary Get an out-of-range incident with its reading timeline
 */
export const getIncidentQueryControllerGet = (
  incidentId: string,
  signal?: AbortSignal,
) => {
  return axiosClient<IncidentDetailResponseDto>({
    url: `/v1/out-of-range-incidents/${incidentId}`,
    method: "GET",
    signal,
  });
};

export const getGetIncidentQueryControllerGetQueryKey = (
  incidentId: string,
) => {
  return [`/v1/out-of-range-incidents/${incidentId}`] as const;
};

export const getGetIncidentQueryControllerGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetIncidentQueryControllerGetQueryKey(incidentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getIncidentQueryControllerGet>>
  > = ({ signal }) => getIncidentQueryControllerGet(incidentId, signal);

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIncidentQueryControllerGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getIncidentQueryControllerGet>>
>;
export type GetIncidentQueryControllerGetQueryError = void;

export function useGetIncidentQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get an out-of-range incident with its reading timeline
 */

export function useGetIncidentQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetIncidentQueryControllerGetQueryOptions(
    incidentId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Paginated list of incidents, filterable by status (open / resolved / all) and by cold-storage unit. Sorted by openedAt desc. Use the existing `/out-of-range-incidents/open` endpoint for the dashboard summary view.
 * @summary List out-of-range incidents for a nursery (open + history)
 */
export const listIncidentsQueryControllerList = (
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
  signal?: AbortSignal,
) => {
  return axiosClient<ListIncidentsResponseDto>({
    url: `/v1/nurseries/${nurseryId}/out-of-range-incidents`,
    method: "GET",
    params,
    signal,
  });
};

export const getListIncidentsQueryControllerListQueryKey = (
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
) => {
  return [
    `/v1/nurseries/${nurseryId}/out-of-range-incidents`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListIncidentsQueryControllerListQueryOptions = <
  TData = Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
  TError = void,
>(
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListIncidentsQueryControllerListQueryKey(nurseryId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listIncidentsQueryControllerList>>
  > = ({ signal }) =>
    listIncidentsQueryControllerList(nurseryId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: nurseryId !== null && nurseryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListIncidentsQueryControllerListQueryResult = NonNullable<
  Awaited<ReturnType<typeof listIncidentsQueryControllerList>>
>;
export type ListIncidentsQueryControllerListQueryError = void;

export function useListIncidentsQueryControllerList<
  TData = Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
  TError = void,
>(
  nurseryId: string,
  params: undefined | ListIncidentsQueryControllerListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
          TError,
          Awaited<ReturnType<typeof listIncidentsQueryControllerList>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListIncidentsQueryControllerList<
  TData = Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
  TError = void,
>(
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
          TError,
          Awaited<ReturnType<typeof listIncidentsQueryControllerList>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListIncidentsQueryControllerList<
  TData = Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
  TError = void,
>(
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List out-of-range incidents for a nursery (open + history)
 */

export function useListIncidentsQueryControllerList<
  TData = Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
  TError = void,
>(
  nurseryId: string,
  params?: ListIncidentsQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listIncidentsQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListIncidentsQueryControllerListQueryOptions(
    nurseryId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Mints a short-lived signed token, calls the pdf-generator-api with the print-ready URL, returns the GCS signed URL. Same authorization as the regular incident detail endpoint (manager / org admin / platform admin). Returns 503 if the PDF service is unconfigured or unreachable.
 * @summary Generate a PDF of an incident and return a signed download URL
 */
export const getIncidentPdfQueryControllerGet = (
  incidentId: string,
  signal?: AbortSignal,
) => {
  return axiosClient<GetIncidentPdfResponseDto>({
    url: `/v1/out-of-range-incidents/${incidentId}/pdf`,
    method: "GET",
    signal,
  });
};

export const getGetIncidentPdfQueryControllerGetQueryKey = (
  incidentId: string,
) => {
  return [`/v1/out-of-range-incidents/${incidentId}/pdf`] as const;
};

export const getGetIncidentPdfQueryControllerGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetIncidentPdfQueryControllerGetQueryKey(incidentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>
  > = ({ signal }) => getIncidentPdfQueryControllerGet(incidentId, signal);

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIncidentPdfQueryControllerGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>
>;
export type GetIncidentPdfQueryControllerGetQueryError = void;

export function useGetIncidentPdfQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentPdfQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentPdfQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Generate a PDF of an incident and return a signed download URL
 */

export function useGetIncidentPdfQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentPdfQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetIncidentPdfQueryControllerGetQueryOptions(
    incidentId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the incident detail without requiring a Bearer token. Authorisation is provided by the short-lived signed `token` query parameter (issued by `GET /v1/out-of-range-incidents/:id/pdf`). Use the regular detail endpoint for any other read.
 * @summary Token-gated incident read for the print page
 */
export const getIncidentForPrintQueryControllerGet = (
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  signal?: AbortSignal,
) => {
  return axiosClient<IncidentDetailResponseDto>({
    url: `/v1/out-of-range-incidents/${incidentId}/print`,
    method: "GET",
    params,
    signal,
  });
};

export const getGetIncidentForPrintQueryControllerGetQueryKey = (
  incidentId: string,
  params?: GetIncidentForPrintQueryControllerGetParams,
) => {
  return [
    `/v1/out-of-range-incidents/${incidentId}/print`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetIncidentForPrintQueryControllerGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetIncidentForPrintQueryControllerGetQueryKey(incidentId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>
  > = ({ signal }) =>
    getIncidentForPrintQueryControllerGet(incidentId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: incidentId !== null && incidentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIncidentForPrintQueryControllerGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>
>;
export type GetIncidentForPrintQueryControllerGetQueryError = void;

export function useGetIncidentForPrintQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentForPrintQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIncidentForPrintQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Token-gated incident read for the print page
 */

export function useGetIncidentForPrintQueryControllerGet<
  TData = Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
  TError = void,
>(
  incidentId: string,
  params: GetIncidentForPrintQueryControllerGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getIncidentForPrintQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetIncidentForPrintQueryControllerGetQueryOptions(
    incidentId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
